import React, {
	useImperativeHandle,
	forwardRef,
	useEffect,
	useState,
} from 'react';
import { Alert, Spin, Typography, Space, Row, Col } from 'antd';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { useGetLastPlantillaConsentimientoQuery } from 'services/PlantillasConsentimientosApi';
import { useDocumentReadTracker } from 'utils/hooks/useDocumentReadTracker';

// Configurar dayjs en español
dayjs.locale('es');

const { Title, Text } = Typography;

/**
 * Componente reutilizable para mostrar la política de privacidad
 * @param {Object} props - Propiedades del componente
 * @param {React.Ref} ref - Referencia para exponer métodos
 */
export const PrivacyPolicyViewer = forwardRef((props, ref) => {
	const {
		onDataLoaded,
		onReadStatusChange,
		showLoading = true,
		showError = true,
		showEmpty = true,
		withLogo = true,
		className,
		style,
		...restProps
	} = props;

	const [availableHeight, setAvailableHeight] = useState(400);
	const [containerHeight, setContainerHeight] = useState(0);

	// Hook para rastrear si el usuario ha leído completamente el documento
	const { hasReaded, contentRef, sentinelRef, measureRef, requiresScroll } =
		useDocumentReadTracker(availableHeight);

	const { data, isFetching, isError, isUninitialized } =
		useGetLastPlantillaConsentimientoQuery(undefined, {
			refetchOnFocus: false,
			refetchOnReconnect: false,
			refetchOnMountOrArgChange: false,
		});

	// Calcular altura disponible dinámicamente
	useEffect(() => {
		const calculateAvailableHeight = () => {
			const viewportHeight = window.innerHeight;
			const minHeight = 300; // Altura mínima para el contenido

			// Si estamos en una pantalla alta, usar más espacio
			if (viewportHeight > 800) {
				// En pantallas altas, usar hasta 75% del viewport
				setAvailableHeight(Math.max(minHeight, viewportHeight * 0.75));
			} else if (viewportHeight > 600) {
				// En pantallas medianas, usar 65% del viewport
				setAvailableHeight(Math.max(minHeight, viewportHeight * 0.65));
			} else {
				// En pantallas pequeñas, usar 50% del viewport
				setAvailableHeight(Math.max(minHeight, viewportHeight * 0.5));
			}
		};

		calculateAvailableHeight();
		window.addEventListener('resize', calculateAvailableHeight);

		return () => {
			window.removeEventListener('resize', calculateAvailableHeight);
		};
	}, []);

	// Actualizar altura del contenedor cuando cambie la altura disponible
	useEffect(() => {
		if (contentRef?.current) {
			const updateContainerHeight = () => {
				const headerHeight = 180; // Altura aproximada del header (reducida)
				const infoHeight = 70; // Altura aproximada de la sección de info (reducida)
				const padding = 60; // Padding total (reducido)

				const totalFixedHeight = headerHeight + infoHeight + padding;
				const contentHeight = Math.max(availableHeight, totalFixedHeight);

				setContainerHeight(contentHeight);
			};

			updateContainerHeight();
		}
	}, [availableHeight, contentRef]);

	// Exponer métodos y datos a través de la ref
	useImperativeHandle(ref, () => ({
		data,
		hasReaded,
		requiresScroll,
		contentRef,
		sentinelRef,
		measureRef,
		isLoading: isFetching || isUninitialized,
		isError,
		availableHeight,
		containerHeight,
	}));

	// Notificar cuando se cargan los datos
	React.useEffect(() => {
		if (data && onDataLoaded) {
			onDataLoaded(data);
		}
	}, [data, onDataLoaded]);

	// Notificar cambios en el estado de lectura
	React.useEffect(() => {
		if (onReadStatusChange) {
			onReadStatusChange({ hasReaded, requiresScroll });
		}
	}, [hasReaded, requiresScroll, onReadStatusChange]);

	const formatDate = dateString => {
		return dayjs(dateString).format('DD [de] MMMM [de] YYYY');
	};

	// Estados de carga
	if (showLoading && (isFetching || isUninitialized)) {
		return (
			<ViewerWrapper className={className} style={style}>
				<div style={{ textAlign: 'center', padding: '3rem' }}>
					<Spin size='large' />
					<Text
						style={{ display: 'block', marginTop: '1rem', color: '#666' }}
					>
						Cargando política de privacidad...
					</Text>
				</div>
			</ViewerWrapper>
		);
	}

	if (showError && isError) {
		return (
			<ViewerWrapper className={className} style={style}>
				<Alert
					message='Error al cargar las plantillas de consentimiento'
					type='error'
					showIcon
					style={{ margin: '2rem 0' }}
				/>
			</ViewerWrapper>
		);
	}

	if (showEmpty && !data) {
		return (
			<ViewerWrapper className={className} style={style}>
				<Alert
					message='No hay plantillas de consentimiento disponibles'
					type='warning'
					showIcon
					style={{ margin: '2rem 0' }}
				/>
			</ViewerWrapper>
		);
	}

	if (!data) {
		return null;
	}

	return (
		<ViewerWrapper
			className={className}
			style={{
				...style,
				minHeight: containerHeight > 0 ? containerHeight : 'auto',
			}}
			{...restProps}
		>
			<ConsentHeader>
				{withLogo && (
					<AppLogo
						src='/img/logo-ortuzar.png'
						alt='Logo Ortuzar Consulting'
					/>
				)}
				<Title level={2} style={{ color: '#1a3353', marginBottom: 0 }}>
					Política de Privacidad
				</Title>
				<Text type='secondary' style={{ fontSize: '14px' }}>
					Última actualización: {formatDate(data.attributes.updatedAt)}
				</Text>
			</ConsentHeader>

			<DocumentInfo>
				<Row gutter={[16, 8]}>
					<Col xs={24} sm={12}>
						<Space>
							<Text strong style={{ color: '#1a3353' }}>
								Versión:
							</Text>
							<Text>{data.attributes.version}</Text>
						</Space>
					</Col>
					<Col xs={24} sm={12}>
						<Space>
							<Text strong style={{ color: '#1a3353' }}>
								Documento:
							</Text>
							<Text>Política de Privacidad para ORTUZAR CONSULTING</Text>
						</Space>
					</Col>
				</Row>
			</DocumentInfo>

			<TextContent
				ref={contentRef}
				style={{
					maxHeight: availableHeight,
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<div ref={measureRef} style={{ flex: 1 }}>
					{data.attributes.texto}
					<ReadSentinel ref={sentinelRef} />
				</div>
			</TextContent>
		</ViewerWrapper>
	);
});

PrivacyPolicyViewer.displayName = 'PrivacyPolicyViewer';

// Styled components
const ViewerWrapper = styled.div`
	max-width: 100%;
	margin: 0 auto;
	padding: 0;
	overflow: hidden;
	display: flex;
	flex-direction: column;
`;

const ConsentHeader = styled.div`
	text-align: center;
	margin-bottom: 0;
	padding: 1.5rem 1.5rem 0.75rem 1.5rem;
	border-bottom: 1px solid #e6ebf1;
	flex-shrink: 0;

	@media (max-width: 768px) {
		padding: 1rem 1rem 0.5rem 1rem;
	}
`;

const AppLogo = styled.img`
	max-width: 160px;
	margin-bottom: 1rem;
	height: auto;

	@media (max-width: 768px) {
		max-width: 140px;
		margin-bottom: 0.75rem;
	}
`;

const DocumentInfo = styled.div`
	background: #f8f9fa;
	padding: 0.75rem 1.5rem;
	border-bottom: 1px solid #e6ebf1;
	flex-shrink: 0;

	@media (max-width: 768px) {
		padding: 0.5rem 1rem;
	}
`;

const TextContent = styled.div`
	padding: 1.5rem 1.5rem;
	overflow-y: auto;
	line-height: 1.8;
	white-space: pre-wrap;
	word-wrap: break-word;
	overflow-wrap: break-word;
	flex: 1;

	h1,
	h2,
	h3,
	h4,
	h5,
	h6 {
		margin-top: 1.5rem;
		margin-bottom: 0.75rem;
		color: #1a3353;
		word-wrap: break-word;
	}

	p {
		margin-bottom: 1rem;
		color: #455560;
		word-wrap: break-word;
	}

	ul,
	ol {
		margin-bottom: 1rem;
		padding-left: 1.5rem;
	}

	li {
		margin-bottom: 0.5rem;
		color: #455560;
		word-wrap: break-word;
	}

	@media (max-width: 768px) {
		padding: 1.25rem 1rem;

		h1,
		h2,
		h3,
		h4,
		h5,
		h6 {
			font-size: 1.1em;
			margin-top: 1rem;
			margin-bottom: 0.5rem;
		}

		p {
			font-size: 0.95em;
			margin-bottom: 0.75rem;
		}

		ul,
		ol {
			padding-left: 1rem;
		}

		li {
			font-size: 0.95em;
			margin-bottom: 0.25rem;
		}
	}

	@media (max-width: 480px) {
		padding: 1rem 0.75rem;

		h1,
		h2,
		h3,
		h4,
		h5,
		h6 {
			font-size: 1em;
		}

		p,
		li {
			font-size: 0.9em;
		}
	}
`;

const ReadSentinel = styled.div`
	height: 1px;
	width: 100%;
	pointer-events: none;
`;

export default PrivacyPolicyViewer;
